<div class="container my-4" *transloco="let t">
  @if (failure(); as failed) {
    <div class="alert alert-warning" role="alert">{{ t(failed.translationKey) }}</div>
  } @else if (loading() && !input()) {
    <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
  } @else if (input(); as current) {
    <div class="row g-4">
      <div class="col-lg-8">
        <!-- What's involved: the key piece of information, read first, as the idea page reads its summary. -->
        @if (current.whatsInvolved) {
          <section class="input-lead mb-4" aria-labelledby="input-whats-involved">
            <h2 id="input-whats-involved" class="input-lead-title">{{ t('inputs.whats_involved') }}</h2>
            <div class="input-lead-body lead fs-4 fw-normal border-start border-4 border-primary ps-3" [innerHTML]="current.whatsInvolved | insightMarkdown"></div>
          </section>
        }

        @if (current.images.hero; as hero) {
          <figure class="input-hero mb-4">
            <img class="img-fluid rounded-4 shadow-sm" [src]="hero | imageUrl: heroTransform" [alt]="hero.altText ?? ''" />
          </figure>
        }

        @if (current.ideas.length) {
          <section class="info-box input-box mb-3" aria-labelledby="input-idea">
            <div class="info-box-icon"><i class="fa-solid fa-lightbulb" aria-hidden="true"></i></div>
            <div class="input-box-body">
              <h2 id="input-idea" class="input-box-title">{{ t('inputs.turning_idea_into_reality') }}</h2>
              <p class="mb-0">
                {{ t('inputs.helps_with_community_idea') }}
                @for (idea of current.ideas; track idea.slug; let last = $last) {
                  <a class="ms-1" [class.fw-semibold]="idea.slug === current.from" [routerLink]="['/ideas', idea.slug]">{{ idea.name }}</a>@if (!last) {,}
                }
              </p>
            </div>
          </section>
        }

        @if (current.myDelivery?.culture || (current.can.take && current.candidateCultures.length)) {
          <section class="info-box input-box mb-3" aria-labelledby="input-cultures">
            <div class="info-box-icon"><i class="fa-solid fa-earth-americas" aria-hidden="true"></i></div>
            <div class="input-box-body">
              <h2 id="input-cultures" class="input-box-title">{{ t('common.cultures') }}</h2>
              @if (current.myDelivery?.culture; as mine) {
                <span class="culture-pill is-chosen">
                  <img class="culture-pill-flag" [src]="flagUrl(mine.flag)" alt="" />{{ mine.name }}
                </span>
              } @else if (current.candidateCultures.length === 1) {
                <span class="culture-pill">
                  <img class="culture-pill-flag" [src]="flagUrl(current.candidateCultures[0].flag)" alt="" />{{ current.candidateCultures[0].name }}
                </span>
              } @else {
                <!-- A choice only when there is one to make: then each pill is a button. -->
                <div class="d-flex flex-wrap gap-2" role="radiogroup" aria-labelledby="input-cultures">
                  @for (culture of current.candidateCultures; track culture.slug) {
                    <button type="button" class="culture-pill" role="radio" [class.is-chosen]="culture.slug === chosen()?.slug"
                      [attr.aria-checked]="culture.slug === chosen()?.slug" (click)="chosenCulture.set(culture.slug)">
                      <img class="culture-pill-flag" [src]="flagUrl(culture.flag)" alt="" />{{ culture.name }}
                    </button>
                  }
                </div>
              }
            </div>
          </section>
        }

        <!-- The subject: the one handed out, or the one most likely to be, for the culture chosen. -->
        @if (current.myDelivery?.subject ?? chosen()?.likelySubject; as subject) {
          <section class="info-box input-box mb-3" aria-labelledby="input-subject">
            <div class="info-box-icon"><i class="fa-solid fa-circle-question" aria-hidden="true"></i></div>
            <div class="input-box-body">
              <h2 id="input-subject" class="input-box-title">{{ t('inputs.subject') }}</h2>
              @if (!current.myDelivery) {
                <p class="small text-muted mb-2">{{ t('inputs.subject_provisional') }}</p>
              }
              <p class="input-subject-question mb-1">{{ subject.question || subject.title }}</p>
              @if (subject.question && subject.title) {
                <p class="small text-muted mb-0">{{ subject.title }}</p>
              }
            </div>
          </section>
        }

        @if (current.deliverIn; as language) {
          <section class="info-box input-box mb-3" aria-labelledby="input-language">
            <div class="info-box-icon"><i class="fa-solid fa-language" aria-hidden="true"></i></div>
            <div class="input-box-body">
              <h2 id="input-language" class="input-box-title">{{ t('common.language') }}</h2>
              <p class="mb-0 d-flex flex-wrap align-items-center gap-2">
                {{ t('inputs.language_of.' + current.kind) }}
                <span class="culture-pill is-static">
                  <img class="culture-pill-flag" [src]="flagUrl(language.flag)" alt="" />{{ language.name ?? (language.iso | uppercase) }}
                </span>
              </p>
              @if (current.kind === 'insight_writing') {
                <p class="small text-muted mt-2 mb-0">{{ t('inputs.deliver_in_help') }}</p>
              }
            </div>
          </section>
        }

        <!-- The task's own description and the instructions, for a reader still making up their mind. -->
        @if (hasDetails()) {
          <div class="mt-4">
            <button type="button" class="btn btn-outline-primary rounded-pill px-4 details-toggle" [attr.aria-expanded]="showDetails()" aria-controls="input-full-details" (click)="showDetails.set(!showDetails())">
              <i class="fa-solid me-2" [class.fa-plus]="!showDetails()" [class.fa-minus]="showDetails()" aria-hidden="true"></i>{{ t(showDetails() ? 'common.show_less' : 'common.show_more') }}
            </button>
          </div>
          @if (showDetails()) {
            <div id="input-full-details" class="input-details mt-3 p-4 rounded-4">
              @if (current.instructions.particular.length) {
                <h2 class="h5">{{ t('inputs.instructions_particular') }}</h2>
                @for (instruction of current.instructions.particular; track $index) {
                  @if (instruction.s) {
                    <p class="fw-semibold mb-1">{{ instruction.s }}</p>
                  }
                  @if (instruction.m) {
                    <div [innerHTML]="instruction.m | insightMarkdown"></div>
                  }
                }
              }

              @if (current.instructions.videos.length) {
                <h2 class="h5 mt-4">{{ t('inputs.videos') }}</h2>
                <ul>
                  @for (video of current.instructions.videos; track $index) {
                    <li>
                      @if (video.pageUrl) {
                        <a [href]="video.pageUrl" target="_blank" rel="noopener">{{ video.name || video.pageUrl }}</a>
                      } @else {
                        {{ video.name }}
                      }
                    </li>
                  }
                </ul>
              }

              <!-- The full instructions when the task has them, otherwise its one-line description. -->
              @if (current.task.longDescription || current.task.description; as taskText) {
                <h2 class="h5 mt-4">{{ current.task.name }}</h2>
                <div class="mb-3" [innerHTML]="taskText | insightMarkdown"></div>
              }

              @if (current.instructions.general.length) {
                <h2 class="h5 mt-4">{{ t('inputs.instructions_general') }}</h2>
                <ul class="mb-0">
                  @for (instruction of current.instructions.general; track $index) {
                    <li>
                      @if (instruction.s) {
                        <span>{{ instruction.s }}</span>
                      }
                      @if (instruction.m) {
                        <div class="small text-muted" [innerHTML]="instruction.m | insightMarkdown"></div>
                      }
                    </li>
                  }
                </ul>
              }
            </div>
          }
        }
      </div>

      <div class="col-lg-4">
        <div class="input-take card border-0 shadow-sm">
          <div class="card-body p-4 text-center">
            <div class="input-take-icon mx-auto mb-3"><i [class]="kindIcon()" aria-hidden="true"></i></div>

            @if (current.task.creditsMax) {
              <p class="mb-3">
                <span class="badge rounded-pill text-bg-light border fs-6 fw-normal">
                  <i class="fa-solid fa-coins me-1 text-warning" aria-hidden="true"></i>{{ t('inputs.credits') }}:
                  {{ current.task.creditsMin === current.task.creditsMax ? current.task.creditsMax : current.task.creditsMin + '–' + current.task.creditsMax }}
                </span>
              </p>
            }

            @if (current.myDelivery; as mine) {
              <p class="mb-3">
                <span class="text-muted">{{ t('inputs.your_work') }}:</span>
                @if (myState(); as state) {
                  <span class="badge badge-pill ms-1" [class]="state.badgeClasses">
                    <i [class]="state.iconClasses" aria-hidden="true"></i>
                    {{ t(state.translationKey) }}
                  </span>
                }
              </p>
              @if (!mine.submittedAt) {
                <button type="button" class="btn btn-primary btn-lg rounded-pill w-100" (click)="goToWork()">{{ t('inputs.continue') }}</button>
              }
            } @else if (needsIdea()) {
              <p class="mb-2 text-start">{{ t('inputs.choose_idea') }}</p>
              @if (current.ideas.length) {
                <ul class="list-unstyled text-start mb-0">
                  @for (idea of current.ideas; track idea.slug) {
                    <li class="mb-1">
                      <i class="fa-solid fa-lightbulb text-primary me-2" aria-hidden="true"></i>
                      <a [routerLink]="[]" [queryParams]="{ from: idea.slug }">{{ idea.name }}</a>
                    </li>
                  }
                </ul>
              } @else {
                <p class="small text-muted mb-0">{{ t('inputs.no_longer_offered') }}</p>
              }
            } @else if (current.can.take) {
              <button type="button" class="btn btn-primary btn-lg rounded-pill w-100" [disabled]="taking() || (current.kind !== 'feature_review' && !chosen())" (click)="take()">
                {{ t('inputs.take') }}
              </button>
            } @else if (!current.offered) {
              <p class="mb-0">{{ t('inputs.no_longer_offered') }}</p>
            } @else {
              <!-- Not for this reader: say so kindly, and point to work they can do. -->
              <button type="button" class="btn btn-primary btn-lg rounded-pill w-100" disabled>{{ t('inputs.take') }}</button>
              <p class="mt-3 mb-2">{{ t('inputs.not_available') }}</p>
              @if (current.alternative; as alternative) {
                <a class="fw-semibold" [routerLink]="['/contributions/inputs', alternative]" [queryParams]="{ from: current.from }">
                  <i class="fa-solid fa-compass me-1" aria-hidden="true"></i>{{ t('inputs.find_alternative') }}
                </a>
              } @else {
                <a class="fw-semibold" routerLink="/contributions">
                  <i class="fa-solid fa-compass me-1" aria-hidden="true"></i>{{ t('inputs.find_alternative') }}
                </a>
              }
            }

            @if (actionFailure(); as failed) {
              <div class="alert alert-danger mt-3 mb-0 text-start" role="alert">{{ t(failed.translationKey) }}</div>
            }
          </div>
        </div>
      </div>
    </div>
  }
</div>
